/**
 * peopleSort — UNE seule source d'ordre pour les gens d'un groupe (musiciens et crew).
 *
 * POURQUOI CE MODULE (2026-10-17) : l'ordre « main d'abord, session ensuite,
 * guest en dernier, puis `order`, puis `name` » était recopié à plusieurs
 * endroits de `RiderPage.tsx` (`selected`, `selectedCrew`) ET manquait aux deux
 * listes de cases du rider preview (`sortedMusicians` triait par `order` SEUL,
 * `crew` n'était pas trié du tout). Résultat mesuré sur les données réelles :
 * un `session` (Tim, order 5) passait après tous les `main` de KADEBOSTANY, et
 * Lucien (main, order 2) de SAINT STACY passait après Nicolas/Samuel
 * (session, order 0).
 *
 * Règle : toute liste de personnes affichée ou imprimée passe par ces
 * comparateurs — ne JAMAIS ré-écrire un 3ᵉ tri local (garde permanente :
 * `src/pages/__tests__/RiderPage.peopleOrder.test.tsx`).
 *
 * `base_role` arrive parfois en snake_case depuis des lignes anciennes (même
 * repli que ShowDetailPage) → TOUJOURS passer par `baseRoleOf()`.
 */

/** Rang des musiciens : les permanents d'abord, les invités en dernier. */
export const MUSICIAN_ROLE_RANK: Record<string, number> = { main: 0, session: 1, guest: 2 };
/** Rang du crew : pas de `guest` pour le crew (décision 2026-08-09). */
export const CREW_ROLE_RANK: Record<string, number> = { main: 0, session: 1 };

/** `main` par défaut ; repli snake_case `base_role` pour les lignes anciennes. */
export const baseRoleOf = (m: { baseRole?: string; base_role?: string }): string =>
  m.baseRole ?? m.base_role ?? 'main';

interface SortablePerson {
  baseRole?: string;
  base_role?: string;
  order?: number;
  name?: string;
}

/** main → session → guest, puis `order`, puis `name` (dernier recours). */
export const compareMusicians = (a: SortablePerson, b: SortablePerson): number => {
  const ra = MUSICIAN_ROLE_RANK[baseRoleOf(a)] ?? 0;
  const rb = MUSICIAN_ROLE_RANK[baseRoleOf(b)] ?? 0;
  if (ra !== rb) return ra - rb;
  const oa = a.order ?? 0;
  const ob = b.order ?? 0;
  if (oa !== ob) return oa - ob;
  return (a.name || '').localeCompare(b.name || '');
};

/** main → session, puis `order`, puis `name` (dernier recours). */
export const compareCrew = (a: SortablePerson, b: SortablePerson): number => {
  const ra = CREW_ROLE_RANK[baseRoleOf(a)] ?? 0;
  const rb = CREW_ROLE_RANK[baseRoleOf(b)] ?? 0;
  if (ra !== rb) return ra - rb;
  const oa = a.order ?? 0;
  const ob = b.order ?? 0;
  if (oa !== ob) return oa - ob;
  return (a.name || '').localeCompare(b.name || '');
};

/** Copie triée (l'entrée n'est jamais mutée). */
export const sortMusicians = <T extends SortablePerson>(list: T[]): T[] =>
  [...list].sort(compareMusicians as (a: T, b: T) => number);

/** Copie triée (l'entrée n'est jamais mutée). */
export const sortCrew = <T extends SortablePerson>(list: T[]): T[] =>
  [...list].sort(compareCrew as (a: T, b: T) => number);
